import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Building2, Users as UsersIcon, CheckCircle, XCircle, Eye, Mail, Phone, Search, 
  Download, FileSpreadsheet, ChevronLeft, ChevronRight, FileText, MapPin, Globe, 
  Briefcase, Calendar, RefreshCw, Shield, User, Image, ExternalLink, Trash2, Hash, Edit, Save, Loader2, Clock, Send
} from 'lucide-react';
import api, { getBackendUrl, getAssetUrl } from '@/utils/api';
import FileUploader from '@/components/FileUploader';

const EmployerManagement = ({ mode = 'admin', executiveId = null }) => {
  const isExecutive = mode === 'executive';
  const apiBase = isExecutive ? '/executive' : '/admin';
  const extraParams = isExecutive && executiveId ? `executive_id=${executiveId}` : '';
  
  const [employers, setEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [profileStatusFilter, setProfileStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'edited' or 'deleted'
  
  // Edited profiles state
  const [editedProfiles, setEditedProfiles] = useState([]);
  const [editedProfilesCount, setEditedProfilesCount] = useState(0);
  const [editedPage, setEditedPage] = useState(1);
  const [editedTotalPages, setEditedTotalPages] = useState(0);
  const [reviewingEmployer, setReviewingEmployer] = useState(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;
  
  // Confirmation dialog states
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [pendingActionId, setPendingActionId] = useState(null);
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Multi-select states
  const [selectedEmployers, setSelectedEmployers] = useState(new Set());
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [serialUpdateLoading, setSerialUpdateLoading] = useState(false);
  
  // Profile reminder states
  const [sendingReminder, setSendingReminder] = useState(null);
  
  // Edit employer states
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingEmployer, setEditingEmployer] = useState(null);
  const [editFormData, setEditFormData] = useState({
    company_name: '',
    industry_type: '',
    industry_sector: '',
    address: '',
    email: '',
    website: '',
    num_employees: '',
    company_info: '',
    logo: '',
    main_mobile: '',
    state: '',
    district: '',
    city: ''
  });

  // Send profile reminder email
  const sendProfileReminder = async (employer) => {
    if (!employer.email) {
      toast.error('Employer does not have an email address');
      return;
    }
    
    // Check if can send (once per day limit)
    const lastSent = employer.profile_reminder_last_sent;
    if (lastSent) {
      const lastSentDate = new Date(lastSent);
      const today = new Date();
      if (lastSentDate.toDateString() === today.toDateString()) {
        toast.error('Reminder already sent today. Please try again tomorrow.');
        return;
      }
    }
    
    setSendingReminder(employer.id);
    try {
      await api.post(`${apiBase}/employers/${employer.id}/send-profile-reminder`);
      toast.success('Profile reminder email sent successfully');
      // Refresh employers to get updated reminder status
      fetchEmployers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  const fetchEmployers = useCallback(async () => {
    try {
      setLoading(true);
      const params = [`page=${currentPage}`, `limit=${limit}`];
      if (searchQuery) params.push(`search=${encodeURIComponent(searchQuery)}`);
      
      // Use 'deleted' status for deleted tab
      if (activeTab === 'deleted') {
        params.push('status=deleted');
      } else {
        if (statusFilter !== 'all') params.push(`status=${statusFilter}`);
        if (profileStatusFilter !== 'all') params.push(`profile_status=${profileStatusFilter}`);
      }
      
      const url = `${apiBase}/employers?` + params.join('&') + (extraParams ? `&${extraParams}` : '');
      
      const response = await api.get(url);
      setEmployers(response.data.data || []);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch employers');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, statusFilter, profileStatusFilter, activeTab]);

  useEffect(() => {
    fetchEmployers();
  }, [fetchEmployers]);

  useEffect(() => {
    // Reset page when filters change
    setCurrentPage(1);
  }, [searchQuery, statusFilter, profileStatusFilter, activeTab]);

  // Fetch edited profiles count on load
  useEffect(() => {
    fetchEditedProfilesCount();
  }, []);

  // Fetch edited profiles when tab changes
  useEffect(() => {
    if (activeTab === 'edited') {
      fetchEditedProfiles();
    }
  }, [activeTab, editedPage]);

  const fetchEditedProfilesCount = async () => {
    try {
      const response = await api.get(`${apiBase}/employers/edited-profiles/count`);
      setEditedProfilesCount(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching edited profiles count:', error);
    }
  };

  const fetchEditedProfiles = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${apiBase}/employers/edited-profiles?page=${editedPage}&limit=20`);
      setEditedProfiles(response.data.data || []);
      setEditedTotalPages(response.data.total_pages || 0);
    } catch (error) {
      toast.error('Failed to fetch edited profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveEdit = async (employerId) => {
    try {
      await api.put(`${apiBase}/employers/${employerId}/approve-edit`);
      toast.success('Profile edit approved');
      fetchEditedProfiles();
      fetchEditedProfilesCount();
    } catch (error) {
      toast.error('Failed to approve edit');
    }
  };

  const handleRejectEdit = async () => {
    if (!reviewingEmployer) return;
    try {
      await api.put(`${apiBase}/employers/${reviewingEmployer.employer_id}/reject-edit`, {
        rejection_reason: rejectReason
      });
      toast.success('Profile edit rejected and reverted to original');
      setShowReviewDialog(false);
      setReviewingEmployer(null);
      setRejectReason('');
      fetchEditedProfiles();
      fetchEditedProfilesCount();
    } catch (error) {
      toast.error('Failed to reject edit');
    }
  };

  const openReviewDialog = (employer) => {
    setReviewingEmployer(employer);
    setShowReviewDialog(true);
    setRejectReason('');
  };

  // Multi-select functions
  const toggleEmployerSelection = (employerId) => {
    const newSelected = new Set(selectedEmployers);
    if (newSelected.has(employerId)) {
      newSelected.delete(employerId);
    } else {
      newSelected.add(employerId);
    }
    setSelectedEmployers(newSelected);
  };

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedEmployers(new Set(employers.map(e => e.id)));
    } else {
      setSelectedEmployers(new Set());
    }
  };

  const handleBulkDelete = async () => {
    if (selectedEmployers.size === 0) {
      toast.error('No employers selected');
      return;
    }
    setShowBulkDeleteDialog(true);
  };

  const confirmBulkDelete = async () => {
    try {
      setBulkDeleteLoading(true);
      const employerIds = Array.from(selectedEmployers);
      await api.post(`${apiBase}/employers/bulk-delete`, { employer_ids: employerIds });
      toast.success(`Successfully deleted ${employerIds.length} employers`);
      setSelectedEmployers(new Set());
      setShowBulkDeleteDialog(false);
      fetchEmployers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete employers');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleUpdateSerialNumbers = async () => {
    try {
      setSerialUpdateLoading(true);
      const response = await api.post(`${apiBase}/employers/backfill-serial-numbers`);
      toast.success(response.data.message || 'Serial numbers updated');
      fetchEmployers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update serial numbers');
    } finally {
      setSerialUpdateLoading(false);
    }
  };

  // Edit employer functions
  const handleEditEmployer = (employer) => {
    const company = employer.company || employer.company_profile || {};
    setEditingEmployer(employer);
    setEditFormData({
      company_name: company.company_name || '',
      industry_type: company.industry_type || '',
      industry_sector: company.industry_sector || '',
      address: company.address || '',
      email: company.email || employer.email || '',
      website: company.website || '',
      num_employees: company.num_employees || '',
      company_info: company.company_info || '',
      logo: company.logo || '',
      main_mobile: company.main_mobile || employer.mobile || '',
      state: company.state || '',
      district: company.district || '',
      city: company.city || ''
    });
    setShowEditDialog(true);
  };

  const handleSaveEmployerEdit = async () => {
    try {
      await api.put(`${apiBase}/employers/${editingEmployer.id}/profile`, editFormData);
      toast.success('Employer profile updated successfully');
      setShowEditDialog(false);
      setEditingEmployer(null);
      fetchEmployers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employer');
    }
  };

  const handleApprove = async (employerId) => {
    setPendingActionId(employerId);
    setShowApproveDialog(true);
  };

  const confirmApprove = async () => {
    setActionLoading(true);
    try {
      await api.put(`${apiBase}/employers/${pendingActionId}/approve`);
      toast.success('Employer approved successfully');
      fetchEmployers();
      if (selectedEmployer?.id === pendingActionId) {
        setSelectedEmployer(prev => ({ ...prev, approval_status: 'approved' }));
      }
      setShowApproveDialog(false);
      setPendingActionId(null);
    } catch (error) {
      toast.error('Failed to approve employer');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (employerId) => {
    setPendingActionId(employerId);
    setRejectionNotes('');
    setShowRejectDialog(true);
  };

  const confirmReject = async () => {
    if (!rejectionNotes.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    setActionLoading(true);
    try {
      await api.put(`${apiBase}/employers/${pendingActionId}/reject`, { rejection_notes: rejectionNotes.trim() });
      toast.success('Employer rejected');
      fetchEmployers();
      if (selectedEmployer?.id === pendingActionId) {
        setSelectedEmployer(prev => ({ ...prev, approval_status: 'rejected' }));
      }
      setShowRejectDialog(false);
      setPendingActionId(null);
      setRejectionNotes('');
    } catch (error) {
      toast.error('Failed to reject employer');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = (employerId) => {
    setPendingActionId(employerId);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    setActionLoading(true);
    try {
      await api.delete(`${apiBase}/employers/${pendingActionId}`);
      toast.success('Employer deleted and moved to Deleted tab');
      fetchEmployers();
      setShowDeleteDialog(false);
      setShowDetailDialog(false);
      setPendingActionId(null);
    } catch (error) {
      toast.error('Failed to delete employer');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestore = (employerId) => {
    setPendingActionId(employerId);
    setShowRestoreDialog(true);
  };

  const confirmRestore = async () => {
    setActionLoading(true);
    try {
      await api.put(`${apiBase}/employers/${pendingActionId}/restore`);
      toast.success('Employer restored! Status set to pending for review.');
      fetchEmployers();
      setShowRestoreDialog(false);
      setPendingActionId(null);
    } catch (error) {
      toast.error('Failed to restore employer');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async (employerId, directApprove = false) => {
    const endpoint = directApprove 
      ? `${apiBase}/employers/${employerId}/reactivate-approved`
      : `${apiBase}/employers/${employerId}/reactivate`;
    const message = directApprove 
      ? 'Employer reactivated and approved! They can now login and post jobs.'
      : 'Employer reactivated! Status set to pending for your review.';
    
    try {
      await api.put(endpoint);
      toast.success(message);
      fetchEmployers();
      if (selectedEmployer?.id === employerId) {
        setSelectedEmployer(prev => ({ 
          ...prev, 
          approval_status: directApprove ? 'approved' : 'pending',
          active: true
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate employer');
    }
  };

  const handleLoginAsEmployer = async (employerId, employerName) => {
    try {
      const response = await api.post(`${apiBase}/login-as-employer/${employerId}`);
      const { token, user } = response.data;
      
      // Create employer session data with admin indicator
      const employerSession = {
        token,
        user: { ...user, is_admin_impersonating: true }
      };
      
      // Encode the session data for URL parameter
      const sessionParam = encodeURIComponent(JSON.stringify(employerSession));
      
      // Open employer dashboard in new tab with session
      const employerUrl = `${window.location.origin}/employer/dashboard?admin_session=${sessionParam}`;
      window.open(employerUrl, '_blank');
      
      toast.success(`Opening ${employerName || 'employer'}'s dashboard in new tab`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to login as employer');
    }
  };

  const toggleStatus = async (employerId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    if (currentStatus && !window.confirm('Deactivating this employer will also affect their active jobs. Continue?')) {
      return;
    }
    try {
      await api.put(`${apiBase}/employers/${employerId}/toggle-status`);
      toast.success(`Employer ${action}d successfully`);
      fetchEmployers();
    } catch (error) {
      toast.error(`Failed to ${action} employer`);
    }
  };

  const viewDetails = (employer) => {
    setSelectedEmployer(employer);
    setShowDetailDialog(true);
  };

  const handleExport = async (format) => {
    try {
      let url = `${apiBase}/employers/export?format=${format}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      
      window.open(getBackendUrl() + '/api' + url, '_blank');
      toast.success(`Exporting to ${format.toUpperCase()}...`);
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const getStatusBadge = (employer) => {
    const approvalStatus = employer.approval_status || 'approved';
    const isActive = employer.active !== false;
    
    if (approvalStatus === 'pending') {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Pending Approval</span>;
    }
    if (approvalStatus === 'rejected') {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Rejected</span>;
    }
    if (!isActive) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">Inactive</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Active</span>;
  };

  // Get profile completion badge
  const getProfileBadge = (employer) => {
    const profileCompleted = employer.profile_completed;
    if (profileCompleted) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Complete</span>;
    }
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700" title={`Missing: ${(employer.missing_profile_fields || []).join(', ')}`}>
        Incomplete
      </span>
    );
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return getAssetUrl(path);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get company data from employer object (handles both 'company' and 'company_profile' keys)
  const getCompanyData = (employer) => {
    return employer?.company || employer?.company_profile || {};
  };

  // Get company name with fallback
  const getCompanyName = (employer) => {
    const company = getCompanyData(employer);
    return company?.company_name || employer?.name || 'N/A';
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Employer Management</h1>
          <p className="text-slate-500 text-sm mt-1">{total} employers registered</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {selectedEmployers.size > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleBulkDelete}
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete ({selectedEmployers.size})
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleUpdateSerialNumbers}
            disabled={serialUpdateLoading}
            className="text-amber-600 border-amber-300 hover:bg-amber-50"
            data-testid="update-serial-btn"
          >
            <Hash className="w-4 h-4 mr-1" />
            {serialUpdateLoading ? 'Updating...' : 'Fix Serial Numbers'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchEmployers()}
            className="text-slate-600"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('csv')}
            className="text-slate-600"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('excel')}
            className="text-slate-600"
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-1" />
            Excel
          </Button>
        </div>
      </div>

      {/* Tabs for Active/Deleted */}
      <div className="flex border-b border-slate-200 mb-4">
        <button
          onClick={() => setActiveTab('active')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'active' 
              ? 'border-teal-600 text-teal-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Active Employers
        </button>
        <button
          onClick={() => setActiveTab('deleted')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'deleted' 
              ? 'border-red-600 text-red-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Deleted
        </button>
        <button
          onClick={() => setActiveTab('edited')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'edited' 
              ? 'border-orange-600 text-orange-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Edited Profiles
          {editedProfilesCount > 0 && (
            <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
              {editedProfilesCount}
            </span>
          )}
        </button>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6 border-slate-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                data-testid="employer-search-input"
                placeholder="Search by Company Name, Mobile, Email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          {activeTab === 'active' && (
            <>
              <div className="w-full md:w-48">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter-select">
                    <SelectValue placeholder="Filter by Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending Approval</SelectItem>
                    <SelectItem value="approved">Approved/Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full md:w-48">
                <Select value={profileStatusFilter} onValueChange={setProfileStatusFilter}>
                  <SelectTrigger data-testid="profile-filter-select">
                    <SelectValue placeholder="Profile Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Profiles</SelectItem>
                    <SelectItem value="complete">Profile Complete</SelectItem>
                    <SelectItem value="incomplete">Profile Incomplete</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Main Content - Show different tables based on tab */}
      {activeTab === 'edited' ? (
        /* Edited Profiles Table */
        <Card className="border-slate-200">
          <div className="p-4 border-b border-slate-200 bg-orange-50">
            <h3 className="font-semibold text-orange-800">Edited Profiles Pending Review</h3>
            <p className="text-sm text-orange-600 mt-1">
              These are approved employers who have edited their profiles. Review changes and approve or reject.
            </p>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-14 text-center font-semibold">Sl.No</TableHead>
                  <TableHead className="font-semibold">Serial No</TableHead>
                  <TableHead className="font-semibold">Company Name</TableHead>
                  <TableHead className="font-semibold">Contact Person</TableHead>
                  <TableHead className="font-semibold">Mobile</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">Edited At</TableHead>
                  <TableHead className="text-center font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-600" />
                    </TableCell>
                  </TableRow>
                ) : editedProfiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <p className="text-slate-600">No edited profiles pending review</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  editedProfiles.map((employer, index) => (
                    <TableRow key={employer.id} className="hover:bg-orange-50/50">
                      <TableCell className="text-center font-medium">{(editedPage - 1) * 20 + index + 1}</TableCell>
                      <TableCell>
                        <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">
                          {employer.serial_number || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {employer.company?.logo ? (
                            <img 
                              src={getImageUrl(employer.company.logo)} 
                              alt="" 
                              className="w-10 h-10 rounded-lg object-cover border border-slate-200"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-slate-400" />
                            </div>
                          )}
                          <div>
                            <span className="font-medium text-slate-900">{employer.company?.company_name || '-'}</span>
                            <p className="text-xs text-slate-500">{employer.company?.industry_sector || '-'}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{employer.company?.contact_person_name || employer.name || '-'}</TableCell>
                      <TableCell>{employer.company?.main_mobile || employer.mobile || '-'}</TableCell>
                      <TableCell>{employer.company?.email || employer.email || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-500">{formatDateTime(employer.edited_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openReviewDialog(employer)}
                            className="text-orange-600 hover:text-orange-700 border-orange-300 hover:bg-orange-50"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Review
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleApproveEdit(employer.employer_id)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination for edited profiles */}
          {editedTotalPages > 1 && (
            <div className="flex items-center justify-center gap-2 p-4 border-t border-slate-200">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditedPage(p => Math.max(1, p - 1))}
                disabled={editedPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <span className="text-sm text-slate-600 px-2">
                Page {editedPage} of {editedTotalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditedPage(p => Math.min(editedTotalPages, p + 1))}
                disabled={editedPage === editedTotalPages}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </Card>
      ) : (
      /* Regular Employers Grid Table */
      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10 text-center">
                  <Checkbox
                    checked={employers.length > 0 && selectedEmployers.size === employers.length}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                <TableHead className="w-14 text-center font-semibold">Sl.No</TableHead>
                <TableHead className="font-semibold">Serial No</TableHead>
                <TableHead className="font-semibold">Reg. Date</TableHead>
                <TableHead className="font-semibold">Company Name</TableHead>
                <TableHead className="font-semibold">Contact Person</TableHead>
                <TableHead className="font-semibold">Mobile Number</TableHead>
                <TableHead className="font-semibold">Email ID</TableHead>
                <TableHead className="font-semibold">Industry</TableHead>
                <TableHead className="font-semibold">Last OTP</TableHead>
                <TableHead className="font-semibold">Login Status</TableHead>
                <TableHead className="text-center font-semibold">HRs</TableHead>
                <TableHead className="text-center font-semibold">Jobs</TableHead>
                <TableHead className="font-semibold">Profile</TableHead>
                <TableHead className="font-semibold">Reminder</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="text-center font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={16} className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-2"></div>
                    <p className="text-slate-600">Loading employers...</p>
                  </TableCell>
                </TableRow>
              ) : employers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={16} className="text-center py-12">
                    <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-600">{activeTab === 'deleted' ? 'No deleted employers' : 'No employers found'}</p>
                    <p className="text-slate-400 text-sm mt-1">{activeTab === 'active' ? 'Try adjusting your search or filters' : 'Deleted employers will appear here'}</p>
                  </TableCell>
                </TableRow>
              ) : (
                employers.map((employer, index) => {
                  const company = getCompanyData(employer);
                  return (
                    <TableRow key={employer.id} className={`hover:bg-slate-50 ${selectedEmployers.has(employer.id) ? 'bg-teal-50' : ''}`}>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={selectedEmployers.has(employer.id)}
                          onCheckedChange={() => toggleEmployerSelection(employer.id)}
                          data-testid={`select-employer-${employer.id}`}
                        />
                      </TableCell>
                      <TableCell className="text-center font-medium">
                        {((currentPage - 1) * limit) + index + 1}
                      </TableCell>
                      <TableCell>
                        {employer.serial_number ? (
                          <span className="inline-flex items-center px-2 py-1 bg-teal-50 text-teal-700 rounded-full text-xs font-mono font-medium">
                            {employer.serial_number}
                          </span>
                        ) : (
                          <span className="text-orange-500 text-xs">Missing</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 whitespace-nowrap">
                        {formatDateTime(employer.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {company?.logo ? (
                            <img 
                              src={getImageUrl(company.logo)} 
                              alt="Logo" 
                              className="w-10 h-10 rounded-lg object-cover border border-slate-200"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-teal-600" />
                            </div>
                          )}
                          <div>
                            <span className="font-medium text-slate-900 block">
                              {company?.company_name || employer.name || 'N/A'}
                            </span>
                            {company?.industry_type && (
                              <span className="text-xs text-slate-500">{company.industry_type}</span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-700">
                        {employer.name || '-'}
                      </TableCell>
                      <TableCell className="font-medium">{employer.mobile}</TableCell>
                      <TableCell className="text-slate-600 max-w-[150px] truncate">
                        {company?.email || employer.email || 'N/A'}
                      </TableCell>
                      <TableCell className="text-slate-600 max-w-[120px] truncate">
                        {company?.industry_sector || 'N/A'}
                      </TableCell>
                      <TableCell className="text-slate-600 text-xs whitespace-nowrap">
                        {employer.last_otp_sent_at ? formatDateTime(employer.last_otp_sent_at) : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {employer.has_logged_in ? (
                          <div>
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Logged In</span>
                            <div className="text-xs text-slate-500 mt-1">{formatDateTime(employer.last_login)}</div>
                          </div>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Never</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                          <UsersIcon className="w-3 h-3" />
                          {employer.hr_count || 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center px-2 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                          {employer.active_jobs_count || 0}
                        </span>
                      </TableCell>
                      <TableCell>{getProfileBadge(employer)}</TableCell>
                      <TableCell>
                        {/* Reminder column - only show for incomplete profiles */}
                        {!employer.profile_completed ? (
                          <div className="text-xs">
                            {employer.profile_reminder_count > 0 ? (
                              <div className="space-y-1">
                                <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                                  Sent ({employer.profile_reminder_count}x)
                                </span>
                                <div className="text-slate-500">
                                  {employer.profile_reminder_last_sent ? new Date(employer.profile_reminder_last_sent).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                                </div>
                              </div>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">Pending</span>
                            )}
                            {employer.email && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => sendProfileReminder(employer)}
                                disabled={sendingReminder === employer.id}
                                className="mt-1 text-xs text-teal-600 hover:text-teal-700 h-6 px-2"
                                title="Send Profile Reminder"
                              >
                                {sendingReminder === employer.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <>
                                    <Send className="w-3 h-3 mr-1" />
                                    Send
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {activeTab === 'deleted' ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Deleted</span>
                        ) : (
                          getStatusBadge(employer)
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          {activeTab === 'deleted' ? (
                            /* Deleted tab actions - only Restore */
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRestore(employer.id)}
                              className="text-green-600 hover:text-green-700 border-green-300 hover:bg-green-50"
                              title="Restore Employer"
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Restore
                            </Button>
                          ) : (
                            /* Active tab actions */
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => viewDetails(employer)}
                                data-testid={`view-employer-${employer.id}`}
                                className="text-slate-600 hover:text-teal-600"
                                title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              
                              {/* Edit button - all employers can be edited */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditEmployer(employer)}
                                data-testid={`edit-employer-${employer.id}`}
                                className="text-blue-600 hover:text-blue-700"
                                title="Edit Employer"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                          
                              {/* Login as Employer button - only for approved employers */}
                              {employer.approval_status === 'approved' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleLoginAsEmployer(employer.id, employer.company?.company_name || employer.name)}
                                  data-testid={`login-as-employer-${employer.id}`}
                                  className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                  title="Login as Employer (Opens in new tab)"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                              )}
                          
                              {employer.approval_status === 'pending' ? (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleApprove(employer.id)}
                                data-testid={`approve-employer-${employer.id}`}
                                className="bg-green-600 hover:bg-green-700 text-white h-8 w-8 p-0"
                                title="Approve"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleReject(employer.id)}
                                data-testid={`reject-employer-${employer.id}`}
                                className="bg-red-600 hover:bg-red-700 text-white h-8 w-8 p-0"
                                title="Reject"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          ) : employer.approval_status === 'rejected' ? (
                            // Reactivate button for rejected employers
                            <Button
                              size="sm"
                              onClick={() => handleReactivate(employer.id, true)}
                              data-testid={`reactivate-employer-${employer.id}`}
                              className="bg-amber-600 hover:bg-amber-700 text-white h-8 px-2"
                              title="Reactivate & Approve"
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Reactivate
                            </Button>
                          ) : employer.approval_status === 'approved' ? (
                            <Button
                              size="sm"
                              onClick={() => toggleStatus(employer.id, employer.active)}
                              data-testid={`toggle-employer-${employer.id}`}
                              className={employer.active 
                                ? 'bg-red-600 hover:bg-red-700 text-white h-8 px-2' 
                                : 'bg-green-600 hover:bg-green-700 text-white h-8 px-2'}
                              title={employer.active ? 'Deactivate' : 'Activate'}
                            >
                              {employer.active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                            </Button>
                          ) : null}
                          
                          {/* Delete button for incomplete profiles */}
                          {!employer.profile_completed && (
                            <Button
                              size="sm"
                              onClick={() => handleDelete(employer.id)}
                              data-testid={`delete-employer-${employer.id}`}
                              className="bg-slate-600 hover:bg-slate-700 text-white h-8 w-8 p-0 ml-1"
                              title="Delete Incomplete Profile"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <div className="text-sm text-slate-600">
              Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, total)} of {total} employers
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <span className="text-sm text-slate-600 px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>
      )}

      {/* Review Edit Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Edit className="w-5 h-5 text-orange-600" />
              Review Profile Changes
            </DialogTitle>
            <DialogDescription>
              Compare original and edited data below. Approve to keep changes or Reject to revert.
            </DialogDescription>
          </DialogHeader>
          {reviewingEmployer && (
            <div className="space-y-6">
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                {reviewingEmployer.company?.logo && (
                  <img 
                    src={getImageUrl(reviewingEmployer.company.logo)} 
                    alt="" 
                    className="w-16 h-16 rounded-lg object-cover border border-slate-200"
                  />
                )}
                <div>
                  <h3 className="font-semibold text-lg">{reviewingEmployer.company?.company_name}</h3>
                  <p className="text-sm text-slate-500">Serial: {reviewingEmployer.serial_number}</p>
                  <p className="text-sm text-slate-500">Edited: {formatDateTime(reviewingEmployer.edited_at)}</p>
                </div>
              </div>

              {/* Changes Table */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-100">
                      <TableHead className="font-semibold">Field</TableHead>
                      <TableHead className="font-semibold text-red-600">Original Value</TableHead>
                      <TableHead className="font-semibold text-green-600">New Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[
                      { key: 'company_name', label: 'Company Name' },
                      { key: 'industry_type', label: 'Industry Type' },
                      { key: 'industry_sector', label: 'Sector' },
                      { key: 'email', label: 'Email' },
                      { key: 'main_mobile', label: 'Mobile' },
                      { key: 'address', label: 'Address' },
                      { key: 'website', label: 'Website' },
                      { key: 'num_employees', label: 'No. of Employees' },
                      { key: 'contact_person_name', label: 'Contact Person' },
                      { key: 'state', label: 'State' },
                      { key: 'district', label: 'District' },
                      { key: 'city', label: 'City' },
                    ].map(field => {
                      const original = reviewingEmployer.original_data?.[field.key] || '-';
                      const current = reviewingEmployer.company?.[field.key] || '-';
                      const isChanged = original !== current;
                      
                      return (
                        <TableRow key={field.key} className={isChanged ? 'bg-yellow-50' : ''}>
                          <TableCell className="font-medium">{field.label}</TableCell>
                          <TableCell className={isChanged ? 'text-red-600' : 'text-slate-500'}>{original}</TableCell>
                          <TableCell className={isChanged ? 'text-green-600 font-medium' : 'text-slate-500'}>{current}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Rejection Reason */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  Rejection Reason (required if rejecting)
                </label>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter reason for rejection..."
                  rows={3}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setShowReviewDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => handleApproveEdit(reviewingEmployer.employer_id)}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve Changes
                </Button>
                <Button 
                  onClick={handleRejectEdit}
                  disabled={!rejectReason.trim()}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject & Revert
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Employer Details</DialogTitle>
          </DialogHeader>
          {selectedEmployer && (
            <EmployerDetailView 
              employer={selectedEmployer}
              getImageUrl={getImageUrl}
              formatDate={formatDate}
              formatDateTime={formatDateTime}
              getStatusBadge={getStatusBadge}
              getCompanyData={getCompanyData}
              onApprove={() => handleApprove(selectedEmployer.id)}
              onReject={() => handleReject(selectedEmployer.id)}
              onReactivate={() => handleReactivate(selectedEmployer.id, true)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              Confirm Approval
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-700">
              Are you sure you want to <strong>approve</strong> this employer?
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Once approved, the employer can login and post jobs on the platform.
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowApproveDialog(false)} className="flex-1" disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={confirmApprove} className="flex-1 bg-green-600 hover:bg-green-700" disabled={actionLoading}>
              {actionLoading ? 'Approving...' : 'Yes, Approve'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Reject Employer
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-slate-700">
              Please provide a reason for rejecting this employer.
            </p>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={rejectionNotes}
                onChange={(e) => setRejectionNotes(e.target.value)}
                placeholder="Enter the reason for rejection (this will be visible to the employer)"
                rows={3}
                className="w-full"
              />
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                <strong>Note:</strong> This reason will be visible to the employer.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowRejectDialog(false)} className="flex-1" disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={confirmReject} className="flex-1 bg-red-600 hover:bg-red-700" disabled={actionLoading || !rejectionNotes.trim()}>
              {actionLoading ? 'Rejecting...' : 'Reject Employer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Delete Employer
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-700">
              Are you sure you want to <strong>delete</strong> this employer?
            </p>
            <p className="text-sm text-slate-500 mt-2">
              The employer will be moved to the "Deleted" tab and can be restored later if needed.
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="flex-1" disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={confirmDelete} className="flex-1 bg-red-600 hover:bg-red-700" disabled={actionLoading}>
              {actionLoading ? 'Deleting...' : 'Yes, Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <RefreshCw className="w-5 h-5" />
              Restore Employer
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-700">
              Are you sure you want to <strong>restore</strong> this employer?
            </p>
            <p className="text-sm text-slate-500 mt-2">
              The employer will be moved back to the active list with "Pending" approval status for review.
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowRestoreDialog(false)} className="flex-1" disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={confirmRestore} className="flex-1 bg-green-600 hover:bg-green-700" disabled={actionLoading}>
              {actionLoading ? 'Restoring...' : 'Yes, Restore'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete {selectedEmployers.size} Employers?
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 text-sm">
            Are you sure you want to delete {selectedEmployers.size} selected employer(s)? 
            This will soft-delete them and they can be restored from the "Deleted" tab.
          </p>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowBulkDeleteDialog(false)} className="flex-1" disabled={bulkDeleteLoading}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmBulkDelete} className="flex-1" disabled={bulkDeleteLoading}>
              {bulkDeleteLoading ? 'Deleting...' : `Delete ${selectedEmployers.size} Employers`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Employer Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Edit className="w-5 h-5" />
              Edit Employer Profile
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Logo Display and Upload */}
              <div className="col-span-2">
                <FileUploader
                  label="Company Logo"
                  accept="image/*"
                  uploadEndpoint="/upload/logo"
                  currentFile={editFormData.logo}
                  onUploadSuccess={(path) => setEditFormData({...editFormData, logo: path})}
                  onRemove={() => setEditFormData({...editFormData, logo: ''})}
                  maxSizeMB={1}
                  helpText="Logo will be auto-cropped to a 1:1 square. Pick Center Crop or White-padded before uploading."
                  squareCrop={true}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Company Name</label>
                <Input
                  value={editFormData.company_name}
                  onChange={(e) => setEditFormData({...editFormData, company_name: e.target.value})}
                  placeholder="Company name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Industry Type</label>
                <Select value={editFormData.industry_type} onValueChange={(v) => setEditFormData({...editFormData, industry_type: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select industry type" />
                  </SelectTrigger>
                  <SelectContent>
                    {['Manufacturing', 'IT/Software', 'Healthcare', 'Education', 'Retail', 'Construction', 'Finance', 'Others'].map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Industry Sector</label>
                <Input
                  value={editFormData.industry_sector}
                  onChange={(e) => setEditFormData({...editFormData, industry_sector: e.target.value})}
                  placeholder="e.g., Automobile, Software Services"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Number of Employees</label>
                <Select value={editFormData.num_employees} onValueChange={(v) => setEditFormData({...editFormData, num_employees: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    {['1-10', '11-50', '51-200', '201-500', '500+'].map(range => (
                      <SelectItem key={range} value={range}>{range}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <Input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                  placeholder="Company email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Website</label>
                <Input
                  value={editFormData.website}
                  onChange={(e) => setEditFormData({...editFormData, website: e.target.value})}
                  placeholder="https://www.example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Main Mobile</label>
                <Input
                  value={editFormData.main_mobile}
                  onChange={(e) => setEditFormData({...editFormData, main_mobile: e.target.value})}
                  placeholder="Mobile number"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Address</label>
                <Textarea
                  value={editFormData.address}
                  onChange={(e) => setEditFormData({...editFormData, address: e.target.value})}
                  placeholder="Company address"
                  rows={2}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Company Info</label>
                <Textarea
                  value={editFormData.company_info}
                  onChange={(e) => setEditFormData({...editFormData, company_info: e.target.value})}
                  placeholder="Brief description about the company"
                  rows={3}
                />
              </div>
            </div>
            <div className="flex gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSaveEmployerEdit} className="flex-1 bg-blue-600 hover:bg-blue-700">
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Separate component for detailed view
const EmployerDetailView = ({ 
  employer, 
  getImageUrl, 
  formatDate, 
  formatDateTime, 
  getStatusBadge, 
  getCompanyData,
  onApprove,
  onReject,
  onReactivate
}) => {
  const company = getCompanyData(employer);
  
  return (
    <Tabs defaultValue="company" className="w-full">
      <TabsList className="grid w-full grid-cols-3 mb-6">
        <TabsTrigger value="company" className="flex items-center gap-2">
          <Building2 className="w-4 h-4" />
          Company Info
        </TabsTrigger>
        <TabsTrigger value="documents" className="flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Documents
        </TabsTrigger>
        <TabsTrigger value="hr" className="flex items-center gap-2">
          <UsersIcon className="w-4 h-4" />
          HR Executives ({employer.hr_executives?.length || 0})
        </TabsTrigger>
      </TabsList>

      {/* Company Info Tab */}
      <TabsContent value="company" className="space-y-6">
        {/* Header with Logo */}
        <div className="flex items-start gap-6 p-4 bg-gradient-to-r from-slate-50 to-teal-50 rounded-xl">
          {company?.logo ? (
            <img 
              src={getImageUrl(company.logo)} 
              alt="Logo" 
              className="w-24 h-24 rounded-xl object-cover border-2 border-white shadow-md" 
            />
          ) : (
            <div className="w-24 h-24 bg-teal-100 rounded-xl flex items-center justify-center border-2 border-white shadow-md">
              <Building2 className="w-12 h-12 text-teal-600" />
            </div>
          )}
          <div className="flex-1">
            <h3 className="text-2xl font-bold text-slate-900">{company?.company_name || 'N/A'}</h3>
            <p className="text-slate-600 mt-1">{company?.industry_type} • {company?.industry_sector}</p>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              {getStatusBadge(employer)}
              <span className="text-sm text-slate-500">
                <Calendar className="w-4 h-4 inline mr-1" />
                Registered: {formatDate(employer.created_at)}
              </span>
              {employer.last_login && (
                <span className="text-sm text-slate-500">
                  Last Login: {formatDateTime(employer.last_login)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Contact & Company Details Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Contact Information */}
          <Card className="p-4">
            <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Phone className="w-4 h-4 text-teal-600" />
              Contact Information
            </h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Phone className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Mobile Number</p>
                  <p className="font-medium text-slate-900">{employer.mobile}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Mail className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Email Address</p>
                  <p className="font-medium text-slate-900">{employer.email || 'N/A'}</p>
                </div>
              </div>
              {company?.main_mobile && company.main_mobile !== employer.mobile && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Phone className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Company Phone</p>
                    <p className="font-medium text-slate-900">{company.main_mobile}</p>
                  </div>
                </div>
              )}
              {company?.website && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Globe className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Website</p>
                    <a 
                      href={company.website.startsWith('http') ? company.website : `https://${company.website}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-medium text-teal-600 hover:underline flex items-center gap-1"
                    >
                      {company.website}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Company Details */}
          <Card className="p-4">
            <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-teal-600" />
              Company Details
            </h4>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Industry Type</p>
                  <p className="font-medium text-slate-900">{company?.industry_type || 'N/A'}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Sector</p>
                  <p className="font-medium text-slate-900">{company?.industry_sector || 'N/A'}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">No. of Employees</p>
                  <p className="font-medium text-slate-900">{company?.num_employees || 'N/A'}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Service Type</p>
                  <p className="font-medium text-slate-900">{company?.service_type || 'N/A'}</p>
                </div>
              </div>
              
              {/* Statistics */}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-700">{employer.hr_count || 0}</p>
                  <p className="text-xs text-blue-600">HR Executives</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-700">{employer.active_jobs_count || 0}</p>
                  <p className="text-xs text-green-600">Active Jobs</p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Address */}
        {(company?.address || company?.city) && (
          <Card className="p-4">
            <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-teal-600" />
              Address
            </h4>
            <p className="text-slate-700">
              {[company?.address, company?.city, company?.district, company?.state].filter(Boolean).join(', ')}
            </p>
          </Card>
        )}

        {/* About Company */}
        {company?.company_info && (
          <Card className="p-4">
            <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-teal-600" />
              About Company
            </h4>
            <p className="text-slate-700 whitespace-pre-wrap">{company.company_info}</p>
          </Card>
        )}

        {/* Approval Actions */}
        {employer.approval_status === 'pending' && (
          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <Button
              onClick={onApprove}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve Employer
            </Button>
            <Button
              onClick={onReject}
              variant="outline"
              className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
          </div>
        )}

        {/* Reactivate Actions for Rejected Employers */}
        {employer.approval_status === 'rejected' && (
          <div className="pt-4 border-t border-slate-200">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
              <p className="text-red-700 font-medium">This employer has been rejected</p>
              <p className="text-red-600 text-sm mt-1">
                You can reactivate this employer to allow them to login and update their profile.
              </p>
            </div>
            <Button
              onClick={onReactivate}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reactivate & Approve Employer
            </Button>
          </div>
        )}
      </TabsContent>

      {/* Documents Tab */}
      <TabsContent value="documents" className="space-y-6">
        {/* Company Logo */}
        <Card className="p-4">
          <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Image className="w-4 h-4 text-teal-600" />
            Company Logo
          </h4>
          {company?.logo ? (
            <div className="flex items-center gap-4">
              <img 
                src={getImageUrl(company.logo)} 
                alt="Company Logo" 
                className="w-32 h-32 rounded-xl object-cover border border-slate-200"
              />
              <a 
                href={getImageUrl(company.logo)} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-teal-600 hover:underline text-sm flex items-center gap-1"
              >
                View Full Size <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          ) : (
            <div className="p-8 bg-slate-50 rounded-lg text-center">
              <Image className="w-12 h-12 mx-auto text-slate-300 mb-2" />
              <p className="text-slate-500">No logo uploaded</p>
            </div>
          )}
        </Card>

        {/* ID Proof Document */}
        <Card className="p-4">
          <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Shield className="w-4 h-4 text-amber-600" />
            Company ID Proof
          </h4>
          {company?.id_proof || company?.id_card ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              {(company.id_proof || company.id_card).toLowerCase().endsWith('.pdf') ? (
                <a 
                  href={getImageUrl(company.id_proof || company.id_card)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-3 bg-white border border-amber-300 rounded-lg text-amber-700 hover:bg-amber-100"
                >
                  <FileText className="w-6 h-6" />
                  <span className="font-medium">View ID Proof (PDF)</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
              ) : (
                <div>
                  <img 
                    src={getImageUrl(company.id_proof || company.id_card)} 
                    alt="ID Proof" 
                    className="max-w-full max-h-96 rounded-lg border border-amber-300"
                  />
                  <a 
                    href={getImageUrl(company.id_proof || company.id_card)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-3 text-amber-700 hover:underline text-sm"
                  >
                    View Full Size <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 bg-red-50 border border-red-200 rounded-lg text-center">
              <Shield className="w-12 h-12 mx-auto text-red-300 mb-2" />
              <p className="text-red-600 font-medium">No ID Proof Uploaded</p>
              <p className="text-red-500 text-sm mt-1">The employer has not uploaded an ID proof document yet.</p>
            </div>
          )}
        </Card>
      </TabsContent>

      {/* HR Executives Tab */}
      <TabsContent value="hr" className="space-y-4">
        {employer.hr_executives && employer.hr_executives.length > 0 ? (
          employer.hr_executives.map((hr, index) => (
            <Card key={hr.id || index} className="p-4">
              <div className="flex items-start gap-4">
                {/* HR Profile Image */}
                {hr.profile_image ? (
                  <img 
                    src={getImageUrl(hr.profile_image)} 
                    alt={hr.name} 
                    className="w-16 h-16 rounded-xl object-cover border border-slate-200"
                  />
                ) : (
                  <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                    <User className="w-8 h-8 text-blue-600" />
                  </div>
                )}
                
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h5 className="font-semibold text-slate-900 text-lg">{hr.name || 'N/A'}</h5>
                      <p className="text-slate-500 text-sm">HR Executive #{index + 1}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      hr.active !== false ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {hr.active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  {/* HR Contact Info */}
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">{hr.mobile || 'N/A'}</span>
                    </div>
                    {hr.email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span>{hr.email}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* HR ID Proof */}
                  {hr.id_proof && (
                    <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-xs font-medium text-amber-700 mb-2 flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        HR ID Proof Document
                      </p>
                      {hr.id_proof.toLowerCase().endsWith('.pdf') ? (
                        <a 
                          href={getImageUrl(hr.id_proof)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-amber-300 rounded text-sm text-amber-700 hover:bg-amber-100"
                        >
                          <FileText className="w-4 h-4" />
                          View PDF
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <div>
                          <img 
                            src={getImageUrl(hr.id_proof)} 
                            alt="HR ID Proof" 
                            className="max-w-xs rounded border border-amber-300"
                          />
                          <a 
                            href={getImageUrl(hr.id_proof)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-2 text-amber-700 hover:underline text-xs"
                          >
                            View Full Size <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {!hr.id_proof && (
                    <p className="mt-3 text-xs text-slate-400 italic">No ID proof uploaded for this HR</p>
                  )}
                  
                  {/* Login Info */}
                  <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
                    {hr.last_login ? (
                      <span>Last Login: {formatDateTime(hr.last_login)}</span>
                    ) : (
                      <span className="text-slate-400">Never logged in</span>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))
        ) : (
          <Card className="p-8 text-center">
            <UsersIcon className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h4 className="text-lg font-medium text-slate-700">No HR Executives</h4>
            <p className="text-slate-500 mt-1">This employer has not added any HR executives yet.</p>
          </Card>
        )}
      </TabsContent>
    </Tabs>
  );
};

export default EmployerManagement;
